import React, { useState, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { SearchInput } from '../SearchInput';
import { getGroupedCPTCodesBySubrole, normalizeCPTCode } from '../../constants/CPTCodes';

const CPTCodesDropdownArray = ({
  value,
  options,
  onChange,
  label = 'CPT Codes',
  disabled = false,
  sx = {},
  message = 'Select CPT Codes',
  onOpen = () => { },
  subrole,
  useGroupedDisplay = false,
  renderValue,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const groupedCodes = useMemo(() => {
    if (useGroupedDisplay && subrole) {
      return getGroupedCPTCodesBySubrole(subrole);
    }
    return null;
  }, [subrole, useGroupedDisplay]);

  const groupedOptions = useMemo(() => {
    if (!useGroupedDisplay || !groupedCodes || !options) {
      return null;
    }

    // Normalize codes to strings for consistent comparison
    const subroleCodeNumbers = new Set(
      groupedCodes.subroleCodes.map((code) => normalizeCPTCode(code.code))
    );

    const subroleOptions = options.filter((opt) =>
      subroleCodeNumbers.has(normalizeCPTCode(opt.code))
    );

    const otherOptions = options.filter(
      (opt) => !subroleCodeNumbers.has(normalizeCPTCode(opt.code))
    );

    return {
      subroleLabel: groupedCodes.subroleLabel,
      subroleCodes: subroleOptions,
      otherCodes: otherOptions,
    };
  }, [useGroupedDisplay, groupedCodes, options]);

  const filteredOptions = useMemo(() => {
    const codesToFilter =
      groupedCodes && useGroupedDisplay
        ? [
          ...(groupedOptions?.subroleCodes || []),
          ...(groupedOptions?.otherCodes || []),
        ]
        : options;

    if (!searchTerm) return codesToFilter;

    return codesToFilter.filter((option) => {
      const searchText = `${option.code} ${option.label}`.toLowerCase();
      return searchText.includes(searchTerm.toLowerCase());
    });
  }, [options, searchTerm, groupedCodes, groupedOptions, useGroupedDisplay]);

  // Filter grouped codes for display
  const filteredGroupedCodes = useMemo(() => {
    if (!groupedOptions || !searchTerm) return groupedOptions;

    const filterCodes = (codes) => {
      return codes.filter((option) => {
        const searchText = `${option.code} ${option.label}`.toLowerCase();
        return searchText.includes(searchTerm.toLowerCase());
      });
    };

    return {
      subroleLabel: groupedOptions.subroleLabel,
      subroleCodes: filterCodes(groupedOptions.subroleCodes),
      otherCodes: filterCodes(groupedOptions.otherCodes),
    };
  }, [groupedOptions, searchTerm]);

  // Normalize value to array of strings
  const normalizedValue = useMemo(() => {
    if (!value) return [];
    if (!Array.isArray(value)) return [];
    // Convert all values to strings for consistency
    return value.map((v) => String(v));
  }, [value]);

  // Find the selected options to display their full labels
  const allOptions = useMemo(() => {
    return useGroupedDisplay && groupedOptions
      ? [...groupedOptions.subroleCodes, ...groupedOptions.otherCodes]
      : options;
  }, [useGroupedDisplay, groupedOptions, options]);


  const handleChange = (event) => {
    const selectedValue = event.target.value;
    // Ensure all values are strings
    const normalized = Array.isArray(selectedValue)
      ? selectedValue.map((v) => String(v))
      : [String(selectedValue)];
    if (onChange && typeof onChange === 'function') {
      onChange(normalized);
    }
  };

  const handleMenuItemClick = (optionCode, event) => {
    const optionValue = String(optionCode);

    // Toggle selection: if already selected, remove it; otherwise add it
    const newSelection = normalizedValue.includes(optionValue)
      ? normalizedValue.filter((v) => v !== optionValue)
      : [...normalizedValue, optionValue];

    if (onChange && typeof onChange === 'function') {
      onChange(newSelection);
    }
  };

  const handleOpen = () => {
    setSearchTerm('');
    onOpen();
  };

  const handleClose = () => {
    setSearchTerm('');
  };

  const getSelectedOptions = () => {
    if (!normalizedValue || normalizedValue.length === 0) return [];
    return normalizedValue
      .map((v) => allOptions.find((opt) => String(opt.code) === String(v)))
      .filter(Boolean);
  };

  const selectedOptions = getSelectedOptions();
  const isEmpty = !normalizedValue || normalizedValue.length === 0;

  return (
    <FormControl fullWidth sx={{ ...sx }}>
      <InputLabel
        id='cpt-codes-dropdown-array-label'
        shrink={true}
      >
        {label}
      </InputLabel>
      <Select
        labelId='cpt-codes-dropdown-array-label'
        id='cpt-codes-dropdown-array'
        value={normalizedValue}
        label={label}
        onChange={handleChange}
        disabled={disabled}
        multiple={true}
        displayEmpty
        notched={true}
        renderValue={(selected) => {
          // Use custom renderValue if provided
          if (renderValue) {
            return renderValue(selected, selectedOptions, allOptions);
          }

          // Default renderValue for multiple selection
          if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            return (
              <Typography sx={{ color: 'text.secondary' }}>
                {message}
              </Typography>
            );
          }

          if (Array.isArray(selectedOptions) && selectedOptions.length > 0) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedOptions.map((option) => (
                  <Chip
                    key={option.code}
                    label={`${option.code} - ${option.label || ''}`}
                    size='small'
                    sx={{
                      backgroundColor: '#e9eaff',
                      color: '#9291f0',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            );
          }

          return (
            <Typography sx={{ color: 'text.secondary' }}>{message}</Typography>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: 300, zIndex: 1600 },
          },
          autoFocus: false,
          disablePortal: true,
        }}
        onOpen={handleOpen}
        onClose={handleClose}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--clr-theme-purple)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--clr-theme-purple)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--clr-theme-purple)',
          },
        }}
      >
        {/* Search bar inside dropdown */}
        <ListSubheader
          disableSticky
          onMouseDown={(e) => e.stopPropagation()}
        >
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            message='Search CPT codes...'
          />
        </ListSubheader>

        {/* Render filtered options */}
        {useGroupedDisplay && groupedCodes && filteredGroupedCodes ? (
          <>
            {filteredGroupedCodes.subroleLabel &&
              filteredGroupedCodes.subroleCodes.length > 0 && (
                <>
                  <ListSubheader
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {filteredGroupedCodes.subroleLabel}
                  </ListSubheader>
                  {filteredGroupedCodes.subroleCodes.map((option) => {
                    const optionValue = String(option.code);
                    return (
                      <MenuItem
                        key={option.code}
                        value={optionValue}
                        onClick={(e) => handleMenuItemClick(option.code, e)}
                      >
                        <Box>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {option.code}
                          </Typography>
                          <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary' }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </>
              )}

            {filteredGroupedCodes.otherCodes.length > 0 && (
              <>
                {filteredGroupedCodes.subroleLabel &&
                  filteredGroupedCodes.subroleCodes.length > 0 && (
                    <ListSubheader
                      sx={{
                        fontWeight: 600,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        borderTop: '1px solid',
                      }}
                    >
                      Other
                    </ListSubheader>
                  )}
                {filteredGroupedCodes.otherCodes.map((option) => {
                  const optionValue = String(option.code);
                  return (
                    <MenuItem
                      key={option.code}
                      value={optionValue}
                      onClick={(e) => handleMenuItemClick(option.code, e)}
                    >
                      <Box>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {option.code}
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{ color: 'text.secondary' }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </>
            )}
            {filteredGroupedCodes.subroleCodes.length === 0 &&
              filteredGroupedCodes.otherCodes.length === 0 && (
                <MenuItem disabled>
                  <Typography
                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    No CPT codes found
                  </Typography>
                </MenuItem>
              )}
          </>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const optionValue = String(option.code);
            return (
              <MenuItem
                key={option.code}
                value={optionValue}
                onClick={(e) => handleMenuItemClick(option.code, e)}
              >
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {option.code}
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {option.label}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>
            <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              No CPT codes found
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default CPTCodesDropdownArray;

